import { useEffect, useState } from 'react';

import { supabase } from '@/integrations/supabase/client';

export interface SuperAdminStatus {
  /** True when the user holds the super_admin role. */
  isSuperAdmin: boolean;
  /**
   * True until the role lookup has completed at least once for the current
   * userId. Callers that fail closed on the role (e.g. force-MFA gates)
   * MUST wait for `isLoading === false` before treating `isSuperAdmin` as
   * authoritative — otherwise a slow lookup or transient error opens the
   * gate during the in-flight window.
   */
  isLoading: boolean;
  /**
   * True when the most recent lookup failed. Callers that fail closed should
   * treat `error === true` as if the user might be a super admin.
   */
  error: boolean;
}

interface InternalState {
  isSuperAdmin: boolean;
  error: boolean;
  /**
   * The userId the most recent settled result corresponds to. While
   * `resolvedFor !== userId`, the hook reports `isLoading: true`. This
   * derivation guarantees that any render where `userId` has just changed
   * sees `isLoading: true` synchronously — without waiting for the effect
   * to commit a new state.
   */
  resolvedFor: string | null;
}

export function useSuperAdminStatus(userId: string | null | undefined): SuperAdminStatus {
  const [state, setState] = useState<InternalState>({
    isSuperAdmin: false,
    error: false,
    // No userId resolved yet. Pending state derives from this naturally.
    resolvedFor: null,
  });

  useEffect(() => {
    let isActive = true;

    if (!userId) {
      setState({ isSuperAdmin: false, error: false, resolvedFor: null });
      return () => {
        isActive = false;
      };
    }

    async function resolveSuperAdminRole() {
      let data: unknown = null;
      let queryError: unknown = null;

      try {
        const result = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .eq('role', 'super_admin')
          .maybeSingle();
        data = result.data;
        queryError = result.error;
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error('Error checking super admin role:', error);
        setState({ isSuperAdmin: false, error: true, resolvedFor: userId ?? null });
        return;
      }

      if (!isActive) {
        return;
      }

      if (queryError) {
        console.error('Error checking super admin role:', queryError);
        setState({ isSuperAdmin: false, error: true, resolvedFor: userId ?? null });
        return;
      }

      setState({ isSuperAdmin: !!data, error: false, resolvedFor: userId ?? null });
    }

    resolveSuperAdminRole();

    return () => {
      isActive = false;
    };
  }, [userId]);

  // Derive isLoading synchronously from userId vs the last-resolved id.
  // This prevents the one-render race where userId has just changed but
  // the effect has not yet committed a `setState` to mark it pending.
  const normalizedUserId = userId ?? null;
  const isLoading = normalizedUserId !== null && state.resolvedFor !== normalizedUserId;

  return {
    isSuperAdmin: state.isSuperAdmin,
    isLoading,
    error: state.error,
  };
}

/**
 * Boolean-only convenience wrapper for callers that only care about the
 * resolved value and don't need the loading/error state. Defaults to false
 * during the initial lookup window — callers that need to fail closed must
 * use `useSuperAdminStatus` instead.
 */
export function useIsSuperAdmin(userId: string | null | undefined) {
  return useSuperAdminStatus(userId).isSuperAdmin;
}
